import React, { useEffect } from "react";
import { PieChart, BarChart } from "@mui/x-charts";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  CircularProgress,
} from "@mui/joy";
import { useQuery, gql } from "@apollo/client";
import {
  useActionCable,
  useChannel,
} from "@aersoftware/react-use-action-cable";
import { getWebSocketUrl } from "../utils/actionCableUrl";

const ASSET_OVERVIEW_QUERY = gql`
  query AssetOverview {
    assetOverview {
      assets
      ownedAssets
      unownedAssets
      expiringSoonAssets
      expiredAssets
      validAssets
      pendingOwnershipRequests
      approvedOwnershipRequests
      rejectedOwnershipRequests
      pendingTransferRequests
      approvedTransferRequests
      rejectedTransferRequests
    }
  }
`;

const AssetOverview = () => {
  const { data, loading, error, refetch } = useQuery(ASSET_OVERVIEW_QUERY);

  // Setup WebSocket connection
  const { actionCable } = useActionCable(getWebSocketUrl());
  const channel = useChannel(actionCable);

  useEffect(() => {
    channel.subscribe(
      { channel: "ApplicationChannel" },
      {
        received: (wsData) => {
          if (
            wsData.type === "asset_updated" ||
            wsData.type === "ownership_request_updated" ||
            wsData.type === "transfer_request_updated" ||
            wsData.type === "payment_updated"
          ) {
            refetch();
          }
        },
      }
    );

    return () => {
      channel.unsubscribe();
    };
  }, [refetch]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
          width: "100%",
        }}
      >
        <CircularProgress size="lg" />
      </Box>
    );
  }

  if (error) {
    return (
      <Grid container spacing={2}>
        <Grid xs={12}>
          <Card>
            <CardContent>
              <Typography color="danger">
                Error loading asset overview:{" "}
                {error?.message || "Unknown error"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography level="title-lg" sx={{ mb: 2 }}>
              Ownership Distribution
            </Typography>
            <Box sx={{ width: "100%", height: 300, maxWidth: "100%" }}>
              <PieChart
                series={[
                  {
                    data: [
                      {
                        value: data.assetOverview.ownedAssets,
                        label: "Owned Assets",
                        color: "blue",
                      },
                      {
                        value: data.assetOverview.unownedAssets,
                        label: "Unowned Assets",
                        color: "gray",
                      },
                    ],
                  },
                ]}
                height={300}
                width={undefined}
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography level="title-lg" sx={{ mb: 2 }}>
              Expiration Status
            </Typography>
            <Box sx={{ width: "100%", height: 300, maxWidth: "100%" }}>
              <PieChart
                series={[
                  {
                    data: [
                      {
                        value: data.assetOverview.expiringSoonAssets,
                        label: "Expiring Soon",
                        color: "yellow",
                      },
                      {
                        value: data.assetOverview.expiredAssets,
                        label: "Expired Assets",
                        color: "red",
                      },
                      {
                        value: data.assetOverview.validAssets,
                        label: "Valid Assets",
                        color: "green",
                      },
                    ],
                  },
                ]}
                height={300}
                width={undefined}
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid xs={12}>
        <Card>
          <CardContent>
            <Typography level="title-lg" sx={{ mb: 2 }}>
              Asset Requests
            </Typography>
            <BarChart
              xAxis={[{ data: ["Ownership Requests", "Transfer Requests"] }]}
              series={[
                {
                  data: [
                    data.assetOverview.pendingOwnershipRequests,
                    data.assetOverview.pendingTransferRequests,
                  ],
                  label: "Pending",
                },
                {
                  data: [
                    data.assetOverview.approvedOwnershipRequests,
                    data.assetOverview.approvedTransferRequests,
                  ],
                  label: "Approved",
                },
                {
                  data: [
                    data.assetOverview.rejectedOwnershipRequests,
                    data.assetOverview.rejectedTransferRequests,
                  ],
                  label: "Rejected",
                },
              ]}
              height={300}
            />
          </CardContent>
        </Card>
      </Grid>

      <Grid xs={12}>
        <Card>
          <CardContent>
            <Typography level="title-lg" sx={{ mb: 2 }}>
              Key Metrics Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography level="h1" sx={{ color: "success.500" }}>
                    {data.assetOverview.assets}
                  </Typography>
                  <Typography level="body-sm">Total Assets</Typography>
                </Box>
              </Grid>
              <Grid xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography level="h1" sx={{ color: "primary.500" }}>
                    {data.assetOverview.ownedAssets}
                  </Typography>
                  <Typography level="body-sm">Owned Assets</Typography>
                </Box>
              </Grid>
              <Grid xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography level="h1" sx={{ color: "warning.500" }}>
                    {data.assetOverview.expiringSoonAssets}
                  </Typography>
                  <Typography level="body-sm">Expiring Soon</Typography>
                </Box>
              </Grid>
              <Grid xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography level="h1" sx={{ color: "danger.500" }}>
                    {data.assetOverview.expiredAssets}
                  </Typography>
                  <Typography level="body-sm">Expired Assets</Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default AssetOverview;
